import type { MouseEvent } from 'react'
import { css } from '@emotion/react'
import {
  Button,
  Icon,
  Text,
  DesignToken,
  SpacingToken,
  type ButtonProps,
} from '@livechat/design-system-react-components'
import { Check } from '@livechat/design-system-icons'

import useParamSelector from '@/hooks/useParamSelector'
import {
  getAlternativeFullscreenPath,
  getAppFullscreenWidget,
  getItemById,
} from '@/store/selectors'
import { useTypedDispatch } from '@/store/hooks'
import { logAmplitudeEvent } from '@/store/stats'
import { AMPLITUDE_EVENTS } from '@/constants'
import { navigateToIFrame, PRODUCT } from '@/utils'

const installedCss = css`
  display: flex;
  justify-content: center;
  margin-bottom: 0;
  margin-top: var(${SpacingToken.Spacing1});
  padding-right: var(${SpacingToken.Spacing2});
  gap: var(${SpacingToken.Spacing1});
  color: var(${DesignToken.ActionPositiveDefault});
`

const wrapperCss = css`
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  white-space: nowrap;
`

interface Props extends ButtonProps {
  id: string
  onlyPrimaryButton?: boolean
}

export const OpenFullscreenWidget = ({
  id,
  size,
  onlyPrimaryButton,
  className,
}: Props) => {
  const dispatch = useTypedDispatch()
  const item = useParamSelector(getItemById, id)
  const alternativeFullscreenPath = useParamSelector(
    getAlternativeFullscreenPath,
    id
  )
  const widgetId = useParamSelector(getAppFullscreenWidget, id)?.id

  const openFullscreenWidget = (e: MouseEvent) => {
    dispatch(
      logAmplitudeEvent({
        name: `${AMPLITUDE_EVENTS.OPEN_APP_CLICKED} - open fullscreen widget`,
        params: {
          appId: id,
          appName: item.name,
        },
      })
    )

    if (alternativeFullscreenPath) {
      return navigateToIFrame(
        e,
        `${alternativeFullscreenPath}/${widgetId}`,
        item.product
      )
    }

    const widgetPath =
      item.product === PRODUCT.HelpDesk
        ? `/apps/${item.slug}`
        : `/app/${item.slug}`

    navigateToIFrame(e, widgetPath, item.product)
  }

  return (
    <div css={wrapperCss}>
      <Button
        kind="high-contrast"
        onClick={openFullscreenWidget}
        className={className}
        size={onlyPrimaryButton ? size : 'medium'}
        fullWidth
      >
        Open app
      </Button>
      {!onlyPrimaryButton && (
        <Text css={installedCss} size="md" bold>
          <Icon source={Check} /> Installed
        </Text>
      )}
    </div>
  )
}

export default OpenFullscreenWidget
